import { css } from '@linaria/core';
import { IconNodepool, IconLinkExternal, IconNew, IconClarifai } from 'components/Icons';
import { SelectOption } from 'components/ui_library/Formik/components/FormElements/Select';
import { getComputeProviderInfo, makeCpuTemplate, makeGpuTemplate } from 'modules/Compute/utils';
import { Tooltip } from 'components/PlatformResourceSelector/Tooltip';
export type EntityData = CF.API.Compute.ComputeCluster[] | CF.API.Compute.NodePool[] | CF.API.Compute.Deployment[];
const getNodepoolAccelerator = (instanceType: CF.API.Compute.InstanceType, showMemory: boolean = true, fullMemory: boolean = false) => {
  if (!instanceType) return 'Unavailable';
  const gpuTemplate = makeGpuTemplate({
    instance: instanceType?.compute_info,
    showMemory,
    fullMemory
  });
  const cpuTemplate = makeCpuTemplate({
    instance: instanceType?.compute_info,
    showMemory
  });
  const nodepoolHasGpu = Boolean(instanceType?.compute_info?.accelerator_type?.[0]);
  return nodepoolHasGpu ? gpuTemplate : cpuTemplate;
};
const getClusterOption = (data: CF.API.Compute.ComputeCluster) => {
  const {
    icon
  } = getComputeProviderInfo(data.cloud_provider.id);
  return {
    value: data.id,
    name: <div title={data.id} className={selectedOptionWrapperStyles}>
        <div className={selectedOptionContentStyles}>
          <div className={iconStyles}>{icon}</div>
          <div className={entityNameStyles}>{data.id}</div>
          <div className={subEntityStyles}>{data.region}</div>
        </div>
      </div>,
    content: <Tooltip text={data.description}>
        <div className={clusterOptionWrapperStyles}>
          <div className={iconStyles}>{icon}</div>
          <div className={clusterOptionContentStyles}>
            <div className={entityNameStyles}>{data.id}</div>
            <div className={subEntityStyles}>{data.region}</div>
          </div>
        </div>
      </Tooltip>,
    rawData: data
  };
};
const getNodepoolOption = (data: CF.API.Compute.NodePool) => {
  const gpuTemplate = makeGpuTemplate({
    instance: data.instance_types?.[0]?.compute_info
  });
  const cpuTemplate = makeCpuTemplate({
    instance: data.instance_types?.[0]?.compute_info
  });
  const nodepoolHasGpu = Boolean(data.instance_types?.[0]?.compute_info?.accelerator_type?.[0]);
  return {
    value: data.id,
    name: <div className={selectedOptionWrapperStyles}>
        <div className={selectedOptionContentStyles}>
          <div className={iconStyles}>
            <IconNodepool size={18} />
          </div>
          <div className={entityNameStyles}>{data.id}</div>
          <div className={subEntityStyles}>{nodepoolHasGpu ? gpuTemplate : cpuTemplate}</div>
        </div>
      </div>,
    content: <Tooltip text={data.description}>
        <div className={nodepoolOptionWrapperStyles}>
          <div className={nodepoolOptionContentStyles}>
            <div className={iconStyles}>
              <IconNodepool size={18} />
            </div>
            <div className={entityNameStyles}>{data.id}</div>
            <div className={subEntityStyles}>{nodepoolHasGpu ? gpuTemplate : cpuTemplate}</div>
          </div>
        </div>
      </Tooltip>,
    rawData: data
  };
};
const getDeploymentOption = (data: CF.API.Compute.Deployment) => {
  const nodepool = (data.nodepools as CF.API.Compute.NodePool[])?.[0];
  const nodepoolAccelerator = getNodepoolAccelerator(nodepool?.instance_types?.[0], true, true);
  const nodepoolAcceleratorShort = getNodepoolAccelerator(nodepool?.instance_types?.[0], false, false);
  const cloudProvider = nodepool?.compute_cluster?.cloud_provider?.id;
  const cloudRegion = nodepool?.compute_cluster?.region;
  const {
    icon
  } = getComputeProviderInfo(cloudProvider || '');
  const deployTooltipContent = <div className={deploymentTooltipStyles}>
      <p className="deploy-id">{data.id}</p>
      <div>
        {cloudProvider && cloudRegion && <p className="deploy-details">
            {cloudProvider}/{cloudRegion}
          </p>}
        <p className="deploy-accelerator">{nodepoolAccelerator}</p>
      </div>

      <p>{data.description || 'No Description'}</p>
    </div>;
  return {
    value: data.id,
    name: <div style={{
      padding: '0'
    }} className={deploymentOptionWrapperStyles}>
        <div className={iconStyles}>{icon}</div>
        <div className={entityNameStyles}>{data.id}</div>
        <div className={subEntityStyles}>{nodepoolAcceleratorShort}</div>
      </div>,
    content: <Tooltip text={deployTooltipContent} initialState={{
      placement: 'right'
    }}>
        <div className={deploymentOptionWrapperStyles}>
          <div className={iconStyles}>{icon}</div>
          <div className={entityNameStyles}>{data.id}</div>
          <div className={subEntityStyles}>{nodepoolAcceleratorShort}</div>
        </div>
      </Tooltip>,
    rawData: data
  };
};
export const getCreateNodepoolOption = (cluster: string) => ({
  value: 'create-new-nodepool',
  name: 'Please wait...',
  content: <a className={createOptionStyles} href={`/compute/create?clusterId=${cluster}`}>
      <IconNew size={20} color="#195AFF" />
      <div className="content">
        <div className="item-name">Create a Nodepool</div>
      </div>
      <IconLinkExternal size={20} color="#195AFF" />
    </a>
});
export const CreateClusterOption = () => ({
  value: 'create-new-cluster',
  name: 'Please wait...',
  content: <a className={createOptionStyles} href="/compute/create">
      <IconNew size={20} color="#195AFF" />
      <div className="content">
        <div className="item-name">Create a new cluster</div>
      </div>
      <IconLinkExternal size={20} color="#195AFF" />
    </a>
});
type GenerateQueryParamsToDeployNewModelProps = {
  appId?: string;
  clusterId?: string;
  modelId?: string;
  modelVersionId?: string;
  nodePoolId?: string;
  userId?: string;
};
export const generateDeployNewModelUrl = ({
  appId,
  clusterId,
  modelId,
  modelVersionId,
  nodePoolId,
  userId
}: GenerateQueryParamsToDeployNewModelProps) => {
  const query = [appId && `appId=${encodeURIComponent(appId)}`, clusterId && `computeClusterId=${encodeURIComponent(clusterId)}`, modelId && `modelId=${encodeURIComponent(modelId)}`, modelVersionId && `modelVersionId=${encodeURIComponent(modelVersionId)}`, nodePoolId && `nodePoolId=${encodeURIComponent(nodePoolId)}`, userId && `userId=${encodeURIComponent(userId)}`].filter(Boolean) // Remove undefined or false values
  .join('&'); // Join with '&'

  const modelDeploymentUrl = '/compute/deployments/create';
  return query ? `${modelDeploymentUrl}?${query}` : modelDeploymentUrl;
};
type CreateDeploymentOptionProps = GenerateQueryParamsToDeployNewModelProps;
export const CreateDeploymentOption = ({
  appId,
  modelId,
  modelVersionId,
  userId
}: CreateDeploymentOptionProps) => {
  const deployModelVersionUrl = generateDeployNewModelUrl({
    appId,
    modelId,
    modelVersionId,
    userId
  });
  return {
    value: 'create-new-deployment',
    name: 'Please wait...',
    content: <a className={createOptionStyles} href={deployModelVersionUrl}>
        <IconNew size={20} color="#195AFF" />
        <div className="content">
          <div className="item-name">Create a new deployment</div>
        </div>
        <IconLinkExternal size={20} color="#195AFF" />
      </a>
  };
};
export const CreateDefaultDeployOption = () => ({
  value: '',
  name: <div className={selectedOptionWrapperStyles}>
      <div className={selectedOptionContentStyles}>
        <div className={iconStyles}>
          <IconClarifai />
        </div>
        <div className={entityNameStyles}>Clarifai Shared</div>
      </div>
    </div>,
  content: <Tooltip text="Clarifai's hosted serverless compute offering is always available by default" initialState={{
    placement: 'right'
  }}>
      <div className={deploymentOptionWrapperStyles}>
        <div className={iconStyles}>
          <IconClarifai />
        </div>
        <div className={entityNameStyles}>Clarifai Shared</div>
      </div>
    </Tooltip>
});
export const generateOptions = (entityType: string, entityData: EntityData): SelectOption[] => {
  switch (entityType) {
    case 'cluster':
      return (entityData as CF.API.Compute.ComputeCluster[]).map(getClusterOption);
    case 'nodepool':
      return (entityData as CF.API.Compute.NodePool[]).map(getNodepoolOption);
    case 'deployment':
      return (entityData as CF.API.Compute.Deployment[]).map(data => getDeploymentOption(data));
    default:
      return [];
  }
};

/* ——— SHARED ——— */

const selectedOptionWrapperStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-selectedOptionWrapperStyles-s11vqyp3";
const selectedOptionContentStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-selectedOptionContentStyles-sjd7xdn";
const iconStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-iconStyles-iaqiyma";
const entityNameStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-entityNameStyles-el52z6q";
const subEntityStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-subEntityStyles-s1eis8wz";

/* ——— CREATE ——— */

const createOptionStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-createOptionStyles-cswm8m0";

/* ——— CLUSTERS ——— */

const clusterOptionWrapperStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-clusterOptionWrapperStyles-c9d3z1g";
const clusterOptionContentStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-clusterOptionContentStyles-c1c6o550";

/* ——— NODEPOOLS ——— */

const nodepoolOptionWrapperStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-nodepoolOptionWrapperStyles-n1gk4k51";
const nodepoolOptionContentStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-nodepoolOptionContentStyles-ngvcka";

/* ——— DEPLOYMENTS ——— */

const deploymentOptionWrapperStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-deploymentOptionWrapperStyles-d1qru3xf";
const deploymentTooltipStyles = "odules__Compute__Components__ComputeEntitySelector__utils_tsx-deploymentTooltipStyles-d1geek0u";

require("./utils.linaria.module.css!=!../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./utils.tsx");